/* eslint-disable react/no-children-prop */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Card, CardContent, CardMedia, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { v4 } from "uuid";
import { useParams } from "react-router-dom";
import Markdown from "markdown-to-jsx";
import { getConnectedTrucks, getPurchasedTruck } from "./requestCall";
import { PlayButton } from "../../shared/assets/icons/PlayButton.jsx";
import AddCircularIcon from "../../shared/components/reusableComp/AddCircularIcon";

const streamDetailWrapper = css`
  width: 100%;
  background: #121212;
`;
const descriptionWrapper = css`
  font-size: 14px;
  line-height: 35px;
`;

const streamDetailContentWrapper = (matches) => css`
  padding-bottom: 50px;
  margin: 0 ${!matches && "25%"};
  padding-top: 20px;
  img,
  video {
    width: 100%;
  }
  #subTitlePipeId {
    :before {
      content: "|";
      margin: 0px 8px;
    }
  }
  TableCell {
    border: 1px solid white;
  }
  .MuiTypography-h6 {
    font-size: 24px;
  }
`;

const cardWrapper = (direction, isImageRequiredOnRight, matches) => css`
  background: #121212;
  padding-bottom: 8%;
  .MuiBox-root {
    display: flex;
    align-items: center;
    img {
      width: ${direction === "row" ? "50%" : "100%"};
      order: ${direction === "row" && isImageRequiredOnRight === true ? 2 : -1};
    }
    .MuiCardContent-root {
      padding: 0;
      margin: ${!matches ? "20px 0" : "20px"};
      ${!matches && `width: ${direction === "row" ? "50%" : "100%"};`}
      .MuiTypography-root {
        margin: ${direction === "row"
          ? isImageRequiredOnRight
            ? "10px 0"
            : "10px 20px"
          : "0"};
        color: white;
      }
    }
  }
`;

const bannerVideoWrapper = css`
  position: relative;
  svg {
    pointer-events: none;
    position: absolute;
    top: calc(50% - 50px);
    left: calc(50% - 50px);
    height: 100px;
    width: 100px;
    color: white;
  }
`;
const fontFamilyWrapper = (theme) => css`
  font-family: ${theme.typography.MBCORPOA};
`;
const BannerVideo = ({ videoSrc, showVideoPlayIcon }) => {
  const [play, setPlay] = useState(true);
  const handleClick = () => {
    const video = document.getElementById("bannerVideo");
    setPlay(!video.paused);
  };
  const videoNode = document.getElementById("bannerVideo");

  videoNode?.addEventListener("play", function () {
    setPlay(false);
  });
  videoNode?.addEventListener("pause", function () {
    setPlay(true);
  });
  return (
    <Box css={bannerVideoWrapper}>
      <video
        id="bannerVideo"
        controls="controls"
        src={videoSrc}
        onClick={handleClick}
        autoPlay
      />
      {showVideoPlayIcon && play && <PlayButton />}
    </Box>
  );
};

const renderCard = (
  imgUrl,
  imgName,
  title,
  description,
  direction,
  isImageRequiredOnRight,
  matches
) => {
  return (
    <Card
      css={() => cardWrapper(direction, isImageRequiredOnRight, matches)}
      key={v4()}
    >
      <Box sx={{ display: "flex", flexDirection: direction }}>
        {imgUrl && (
          <CardMedia
            component="img"
            image={`${process.env.REACT_APP_BASEURL}${imgUrl}`}
            alt={imgName}
          />
        )}
        <CardContent style={{ marginInlineEnd: "auto" }}>
          <Typography
            gutterBottom
            variant="h6"
            component="span"
            display="block"
            css={fontFamilyWrapper}
          >
            {title}
          </Typography>
          <Typography
            css={descriptionWrapper}
            variant="body2"
            color="text.secondary"
          >
            <Markdown>{description || ""}</Markdown>
          </Typography>
        </CardContent>
      </Box>
    </Card>
  );
};

const renderBanner = (
  bannerDetail,
  title = "",
  subtitle = "",
  description = "",
  button,
  imageTextTile,
  matches
) => {
  return (
    <Box css={() => streamDetailContentWrapper(matches)}>
      <Box key={v4()} sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            margin: !matches ? "40px 0" : "10px 20px 20px 20px",
            color: "white",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <Typography
              gutterBottom
              variant="h6"
              component="div"
              display={!matches ? "inline" : "block"}
              css={fontFamilyWrapper}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                display: subtitle ? "inline" : "none",
                fontFamily: (theme) => theme.typography.MBCORPOA,
              }}
              id={!matches ? "subTitlePipeId" : "subtitle"}
            >
              {subtitle}
            </Typography>
          </div>
          <Typography css={descriptionWrapper} variant="body2">
            <Markdown>{description}</Markdown>
          </Typography>
        </Box>
        {bannerDetail?.bannerVideoSource ? (
          <BannerVideo
            videoSrc={bannerDetail?.bannerVideoSource}
            showVideoPlayIcon={bannerDetail?.showVideoPlayIcon}
          />
        ) : (
          bannerDetail?.bannerImage?.data?.attributes?.url && (
            <img
              src={`${process.env.REACT_APP_BASEURL}${bannerDetail?.bannerImage?.data?.attributes?.url}`}
            />
          )
        )}
        {bannerDetail?.textTiles?.map((textTile) => (
          <Box
            key={v4()}
            sx={{
              margin: !matches ? "40px 0" : "20px",
              color: "white",
            }}
          >
            {textTile?.heading && (
              <Typography gutterBottom variant="h6" css={fontFamilyWrapper}>
                {textTile?.heading}
              </Typography>
            )}
            <Typography css={descriptionWrapper} variant="body2">
              <Markdown>{textTile?.description}</Markdown>
            </Typography>
            {button?.buttonText && (
              <AddCircularIcon
                buttonCss="margin-top: 20px;font-size: medium;"
                handleClick={() =>
                  button?.buttonLink &&
                  (button?.isExternalLink
                    ? window.open(button?.buttonLink)
                    : (window.location = button?.buttonLink))
                }
                buttonText={button?.buttonText}
                iconCss={{
                  width: "20px",
                  height: "20px",
                  marginRight: "10px",
                  padding: "4px",
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      <Box>
        {imageTextTile?.map((dataItem) =>
          renderCard(
            dataItem?.image?.data?.attributes?.url,
            dataItem?.image?.data?.attributes?.name,
            dataItem?.title,
            dataItem?.description,
            matches ? "column" : "row",
            dataItem?.isImageRequiredOnRight,
            matches
          )
        )}
      </Box>
    </Box>
  );
};

const ConnectedTruck = ({ Slug }) => {
  const { slug } = useParams();
  const pathname = window.location.pathname.split("/")[1];

  window.scrollTo(0, 0);

  const [bannerDetail, setBannerDetail] = useState();
  const [imageTextTile, setImageTexTile] = useState();
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [bannerImage, setBannerImage] = useState();
  const [button, setButton] = useState();
  const matches = useMediaQuery("(max-width: 415px)");

  useEffect(() => {
    let funcToBeCalled;
    switch (pathname) {
      case "purchased-truck":
        funcToBeCalled = getPurchasedTruck;
        break;
      default:
        funcToBeCalled = getConnectedTrucks;
        break;
    }
    slug &&
      funcToBeCalled(slug || Slug).then((res) => {
        const response = res?.data?.data?.attributes;
        document.title = response?.title;
        const banner = response?.body?.filter(
          (item) =>
            item.__component === "components.banner-image-with-text-tiles"
        );
        setBannerDetail(banner[0]);
        setTitle(response?.title);
        setSubtitle(response?.subtitle);
        setDescription(response?.description);
        setBannerImage(response?.bannerImage);
        const imageTextTilesOnly = res?.data?.data?.attributes?.body?.filter(
          (item) => item.__component === "components.image-text-tiles"
        );
        setImageTexTile(imageTextTilesOnly);
        const buttonValue = response?.body.filter(
          (item) => item.__component === "components.button"
        );
        setButton(buttonValue[0]);
      });
  }, [slug]);

  return (
    <Box css={streamDetailWrapper}>
      {bannerImage && (
        <img
          width="100%"
          height="auto"
          src={`${process.env.REACT_APP_BASEURL}${bannerImage?.data?.attributes?.url}`}
        />
      )}
      {renderBanner(
        bannerDetail,
        title,
        subtitle,
        description,
        button,
        imageTextTile,
        matches
      )}
    </Box>
  );
};
export default ConnectedTruck;
